import { useMemo, useState } from 'react'
import { EnrollmentAssessmentDtoStatusEnum, type AssessmentAnswerDto, type EnglishTestDto } from '@/api/generated/models'
import type { EnglishProficiencyStepProps, EnglishTestResult } from './types'
import { getPersistedEnglishTestResult } from './utils'

type UseEnglishProficiencyStateArgs = Pick<EnglishProficiencyStepProps, 'enrollment'>

export function useEnglishProficiencyState({ enrollment }: UseEnglishProficiencyStateArgs) {
  const [isTestModalOpen, setIsTestModalOpen] = useState(false)
  const [testResultOpen, setTestResultOpen] = useState(false)
  const [testResult, setTestResult] = useState<EnglishTestResult | null>(null)
  const [assessmentId, setAssessmentId] = useState<number | null>(null)
  const [defaultTest, setDefaultTest] = useState<EnglishTestDto | null>(null)
  const [initialAssessmentAnswers, setInitialAssessmentAnswers] = useState<AssessmentAnswerDto[]>(
    []
  )
  const [startTestError, setStartTestError] = useState<string | null>(null)
  const [continueError, setContinueError] = useState<string | null>(null)
  const [isContinuingAfterPass, setIsContinuingAfterPass] = useState(false)
  const [retryLockoutMessage, setRetryLockoutMessage] = useState<string | null>(null)

  const persistedTestResult = useMemo(
    () => getPersistedEnglishTestResult(enrollment),
    [enrollment]
  )
  const effectiveTestResult = testResult ?? persistedTestResult
  const showResultScreen = !isTestModalOpen && (testResultOpen || persistedTestResult !== null)
  const testPassed = effectiveTestResult?.status === EnrollmentAssessmentDtoStatusEnum.Passed
  const isRetryLockout = retryLockoutMessage !== null

  const resetTestState = () => {
    setTestResult(null)
    setTestResultOpen(false)
    setStartTestError(null)
    setContinueError(null)
    setRetryLockoutMessage(null)
    setAssessmentId(null)
    setDefaultTest(null)
    setInitialAssessmentAnswers([])
  }

  return {
    assessmentId,
    continueError,
    defaultTest,
    effectiveTestResult,
    initialAssessmentAnswers,
    isContinuingAfterPass,
    isRetryLockout,
    isTestModalOpen,
    persistedTestResult,
    resetTestState,
    retryLockoutMessage,
    setAssessmentId,
    setContinueError,
    setDefaultTest,
    setInitialAssessmentAnswers,
    setIsContinuingAfterPass,
    setIsTestModalOpen,
    setRetryLockoutMessage,
    setStartTestError,
    setTestResult,
    setTestResultOpen,
    showResultScreen,
    startTestError,
    testPassed,
    testResult,
    testResultOpen
  }
}
